import { DetailRequirement, PrgDetail, TraitId } from '@cibo/core'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setLocale } from 'yup'
import { EditMultipleFieldsForm } from '../EditMultipleFields/EditMultipleFields'
import { EditSingleFieldForm } from '../EditSingleField'
import { RemoveDetailRequest, SaveDetailRequest, useRemoveDetail, useSaveDetail } from '../queries'

type BaseModalState = {
  onSuccess?: () => void
  filterInputs?: PrgDetail
}

export type DetailModalState = {
  traitId?: TraitId
  requirement?: DetailRequirement
  resourceId?: string
} & BaseModalState

export type BulkEditModalState = {
  traitId: TraitId
  requirements?: DetailRequirement[]
  resourceIds: string[]
} & BaseModalState

export interface DetailEditingState {
  removeDetail(request: RemoveDetailRequest): Promise<any>
  saveDetail(request: SaveDetailRequest): Promise<any>
  setDetailModal(state: DetailModalState): void
  setBulkEditModal(state: BulkEditModalState): void
}

export const DetailEditingContext = createContext<DetailEditingState>({
  removeDetail: (request: RemoveDetailRequest) => {
    console.log('removeDetail', request)
    return Promise.reject()
  },
  saveDetail: (request: SaveDetailRequest) => {
    console.log('saveDetail', request)
    return Promise.reject()
  },
  setDetailModal: console.log,
  setBulkEditModal: console.log,
})

export const DetailEditingProvider: React.FC<{}> = ({ children }) => {
  const [detailModal, setDetailModal] = useState<DetailModalState | undefined>()
  const [bulkEditModal, setBulkEditModal] = useState<BulkEditModalState | undefined>()
  const { mutateAsync: saveDetail } = useSaveDetail()
  const { mutateAsync: removeDetail } = useRemoveDetail()
  const requestCloseDetail = useCallback(() => setDetailModal(undefined), [])
  const requestCloseBulk = useCallback(() => setBulkEditModal(undefined), [])

  const { t } = useTranslation('@cibo/landmanger/DetailEditingProvider')

  useEffect(() => {
    setLocale({
      mixed: {
        notType: t('invalid'),
      },
    })
  }, [])

  return (
    <DetailEditingContext.Provider
      value={{
        removeDetail,
        saveDetail,
        setDetailModal,
        setBulkEditModal,
      }}
    >
      {children}

      <EditSingleFieldForm {...detailModal} requestClose={requestCloseDetail} />
      <EditMultipleFieldsForm {...bulkEditModal} requestClose={requestCloseBulk} />
    </DetailEditingContext.Provider>
  )
}

export const useDetailEditing = () => useContext(DetailEditingContext)
